import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import { Separator } from '../components/ui/separator';
import { ScrollArea } from '../components/ui/scroll-area';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import {
    BookOpen,
    ArrowLeft,
    Building2,
    User,
    Calendar,
    Clock,
    Plus,
    ExternalLink,
    CheckCircle2,
    Circle,
    Loader2,
    MapPin,
    FileText,
    Video,
    Mic,
    Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

const EditorialDetail = () => {
    const { id } = useParams();
    const { api, user } = useAuth();
    const navigate = useNavigate();
    
    const [project, setProject] = useState(null);
    const [stages, setStages] = useState([]);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [togglingStage, setTogglingStage] = useState(null);
    
    // Event modal
    const [showEventModal, setShowEventModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [eventForm, setEventForm] = useState({
        title: '',
        type: 'interview',
        starts_at: '',
        ends_at: '',
        location: '',
        description: '',
    });

    useEffect(() => {
        fetchProjectData();
    }, [id]);

    const fetchProjectData = async () => {
        setLoading(true);
        try {
            const [projectRes, stagesRes, eventsRes] = await Promise.all([
                api.get(`/editorial-projects/${id}`),
                api.get(`/editorial-projects/${id}/stages`),
                api.get(`/calendar-events?project_id=${id}`),
            ]);
            
            setProject(projectRes.data);
            setStages(stagesRes.data);
            setEvents(eventsRes.data);
        } catch (error) {
            console.error('Failed to fetch project data:', error);
            toast.error('Erreur lors du chargement');
            navigate('/editorial');
        } finally {
            setLoading(false);
        }
    };

    const handleToggleStage = async (stageId) => {
        setTogglingStage(stageId);
        try {
            const response = await api.post(`/editorial-projects/${id}/stages/${stageId}/toggle`);
            
            // Update local state
            setStages(prev => prev.map(s => 
                s.id === stageId 
                    ? { ...s, is_completed: response.data.is_completed }
                    : s
            ));
            setProject(prev => ({ ...prev, progress_percent: response.data.progress_percent }));
            
            toast.success(response.data.is_completed ? 'Étape validée' : 'Étape dévalidée');
        } catch (error) {
            console.error('Failed to toggle stage:', error);
            toast.error('Erreur lors de la mise à jour');
        } finally {
            setTogglingStage(null);
        }
    };

    const handleCreateEvent = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        
        try {
            await api.post('/calendar-events', {
                editorial_project_id: id,
                company_id: project.company_id,
                ...eventForm,
            });
            toast.success('Événement créé');
            setShowEventModal(false);
            setEventForm({
                title: '',
                type: 'interview',
                starts_at: '',
                ends_at: '',
                location: '',
                description: '',
            });
            fetchProjectData();
        } catch (error) {
            console.error('Failed to create event:', error);
            toast.error('Erreur lors de la création');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteEvent = async (eventId) => {
        if (!window.confirm('Supprimer cet événement ?')) return;
        
        try {
            await api.delete(`/calendar-events/${eventId}`);
            toast.success('Événement supprimé');
            fetchProjectData();
        } catch (error) {
            console.error('Failed to delete event:', error);
            toast.error('Erreur lors de la suppression');
        }
    };

    const getMagazineBadge = (magazineLabel) => {
        const colors = {
            'Décideur': 'bg-purple-500/10 text-purple-500',
            'The Place to B': 'bg-blue-500/10 text-blue-500',
            "Le Mag' de l'Immo": 'bg-emerald-500/10 text-emerald-500',
        };
        return (
            <Badge className={colors[magazineLabel] || 'bg-gray-500/10 text-gray-500'}>
                {magazineLabel}
            </Badge>
        );
    };

    const getEventTypeIcon = (type) => {
        const icons = {
            interview: Mic,
            shooting: Video,
            meeting: Users,
            deadline: Calendar,
        };
        return icons[type] || Calendar;
    };

    const getEventTypeLabel = (type) => {
        const labels = {
            interview: 'Interview',
            shooting: 'Shooting',
            meeting: 'Réunion',
            deadline: 'Deadline',
        };
        return labels[type] || type;
    };

    const formatDateTime = (dateString) => {
        return format(parseISO(dateString), 'dd MMM yyyy à HH:mm', { locale: fr });
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="flex items-center gap-4">
                    <Skeleton className="h-10 w-10" />
                    <Skeleton className="h-8 w-64" />
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <Skeleton className="h-96 lg:col-span-2" />
                    <Skeleton className="h-96" />
                </div>
            </div>
        );
    }

    if (!project) return null;

    const completedStages = stages.filter(s => s.is_completed).length;
    const totalStages = stages.length;

    return (
        <div className="space-y-6 animate-fade-in" data-testid="editorial-detail-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigate('/editorial')}
                    className="rounded-full self-start"
                    data-testid="back-btn"
                >
                    <ArrowLeft className="w-5 h-5" />
                </Button>
                <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                        <h1 className="text-2xl font-bold font-heading">{project.title}</h1>
                        {getMagazineBadge(project.magazine_label)}
                        {project.issue_label && (
                            <Badge variant="outline">{project.issue_label}</Badge>
                        )}
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                            <Building2 className="w-4 h-4" />
                            {project.company_name}
                        </span>
                        {project.contact_name && (
                            <span className="flex items-center gap-1">
                                <User className="w-4 h-4" />
                                {project.contact_name}
                            </span>
                        )}
                        {project.deadline && (
                            <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                Deadline: {new Date(project.deadline).toLocaleDateString('fr-FR')}
                            </span>
                        )}
                    </div>
                </div>
                <Button
                    onClick={() => setShowEventModal(true)}
                    className="rounded-full"
                    data-testid="add-event-btn"
                >
                    <Plus className="w-4 h-4 mr-2" />
                    Rendez-vous
                </Button>
            </div>

            {/* Progress Overview */}
            <Card>
                <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h3 className="font-semibold font-heading">Progression globale</h3>
                            <p className="text-sm text-muted-foreground">
                                {completedStages} sur {totalStages} étapes complétées
                            </p>
                        </div>
                        <div className="text-3xl font-bold text-primary">
                            {project.progress_percent}%
                        </div>
                    </div>
                    <Progress value={project.progress_percent} className="h-3" />
                </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Stages Checklist */}
                <Card className="lg:col-span-2">
                    <CardHeader>
                        <CardTitle className="font-heading">Étapes éditoriales</CardTitle>
                        <CardDescription>
                            Cochez les étapes au fur et à mesure de l'avancement
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {stages.map((stage, index) => (
                                <div
                                    key={stage.id}
                                    className={`flex items-center gap-4 p-4 rounded-xl transition-colors ${
                                        stage.is_completed 
                                            ? 'bg-green-500/5 border border-green-500/20' 
                                            : 'bg-muted/50 border border-transparent'
                                    }`}
                                    data-testid={`stage-${stage.id}`}
                                >
                                    <button
                                        onClick={() => handleToggleStage(stage.id)}
                                        disabled={togglingStage === stage.id}
                                        className="flex-shrink-0"
                                    >
                                        {togglingStage === stage.id ? (
                                            <Loader2 className="w-6 h-6 text-primary animate-spin" />
                                        ) : stage.is_completed ? (
                                            <CheckCircle2 className="w-6 h-6 text-green-500" />
                                        ) : (
                                            <Circle className="w-6 h-6 text-muted-foreground hover:text-primary transition-colors" />
                                        )}
                                    </button>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded">
                                                {index + 1}
                                            </span>
                                            <p className={`font-medium ${stage.is_completed ? 'line-through text-muted-foreground' : ''}`}>
                                                {stage.label}
                                            </p>
                                        </div>
                                        {stage.is_completed && stage.completed_by_name && (
                                            <p className="text-xs text-muted-foreground mt-1">
                                                Validé par {stage.completed_by_name}
                                                {stage.completed_at && (
                                                    <> le {new Date(stage.completed_at).toLocaleDateString('fr-FR')}</>
                                                )}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>

                {/* Sidebar - Events & Info */}
                <div className="space-y-6">
                    {/* Events */}
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between pb-2">
                            <CardTitle className="text-lg font-heading">Rendez-vous</CardTitle>
                            <Button
                                size="sm"
                                variant="ghost"
                                className="rounded-full"
                                onClick={() => setShowEventModal(true)}
                            >
                                <Plus className="w-4 h-4" />
                            </Button>
                        </CardHeader>
                        <CardContent>
                            {events.length === 0 ? (
                                <div className="text-center py-6 text-muted-foreground">
                                    <Calendar className="w-10 h-10 mx-auto mb-2 opacity-50" />
                                    <p className="text-sm">Aucun rendez-vous</p>
                                </div>
                            ) : (
                                <ScrollArea className="h-[300px] pr-4">
                                    <div className="space-y-3">
                                        {events.map((event) => {
                                            const Icon = getEventTypeIcon(event.type);
                                            return (
                                                <div
                                                    key={event.id}
                                                    className="p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                                                >
                                                    <div className="flex items-start gap-3">
                                                        <div className="p-2 rounded-lg bg-primary/10">
                                                            <Icon className="w-4 h-4 text-primary" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="font-medium text-sm">{event.title}</p>
                                                            <p className="text-xs text-muted-foreground mt-1">
                                                                {getEventTypeLabel(event.type)}
                                                            </p>
                                                            <div className="flex items-center gap-1 text-xs text-muted-foreground mt-2">
                                                                <Clock className="w-3 h-3" />
                                                                {formatDateTime(event.starts_at)}
                                                            </div>
                                                            {event.location && (
                                                                <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                                                                    <MapPin className="w-3 h-3" />
                                                                    {event.location}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </ScrollArea>
                            )}
                        </CardContent>
                    </Card>

                    {/* Project Info */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-lg font-heading">Informations</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div>
                                <p className="text-sm text-muted-foreground">Créé par</p>
                                <p className="font-medium">{project.created_by_name}</p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Date de création</p>
                                <p className="font-medium">
                                    {new Date(project.created_at).toLocaleDateString('fr-FR')}
                                </p>
                            </div>
                            {project.notes && (
                                <div>
                                    <p className="text-sm text-muted-foreground">Notes</p>
                                    <p className="text-sm mt-1">{project.notes}</p>
                                </div>
                            )}
                            {project.drive_folder_url && (
                                <Button
                                    variant="outline"
                                    className="w-full rounded-full"
                                    asChild
                                >
                                    <a href={project.drive_folder_url} target="_blank" rel="noopener noreferrer">
                                        <ExternalLink className="w-4 h-4 mr-2" />
                                        Ouvrir le dossier Drive
                                    </a>
                                </Button>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Event Modal */}
            <Dialog open={showEventModal} onOpenChange={setShowEventModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading">Nouveau rendez-vous</DialogTitle>
                        <DialogDescription>
                            Planifiez un événement pour ce dossier
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateEvent} className="space-y-4">
                        <div className="space-y-2">
                            <Label>Type</Label>
                            <Select
                                value={eventForm.type}
                                onValueChange={(v) => setEventForm({ ...eventForm, type: v })}
                            >
                                <SelectTrigger className="rounded-lg">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="interview">Interview</SelectItem>
                                    <SelectItem value="shooting">Shooting photo</SelectItem>
                                    <SelectItem value="meeting">Réunion</SelectItem>
                                    <SelectItem value="deadline">Deadline</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Titre *</Label>
                            <Input
                                value={eventForm.title}
                                onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                                placeholder="Ex: Interview dirigeant"
                                required
                                className="rounded-lg"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Début *</Label>
                                <Input
                                    type="datetime-local"
                                    value={eventForm.starts_at}
                                    onChange={(e) => setEventForm({ ...eventForm, starts_at: e.target.value })}
                                    required
                                    className="rounded-lg"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Fin *</Label>
                                <Input
                                    type="datetime-local"
                                    value={eventForm.ends_at}
                                    onChange={(e) => setEventForm({ ...eventForm, ends_at: e.target.value })}
                                    required
                                    className="rounded-lg"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Lieu</Label>
                            <Input
                                value={eventForm.location}
                                onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
                                placeholder="Adresse ou lien visio"
                                className="rounded-lg"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Description</Label>
                            <Textarea
                                value={eventForm.description}
                                onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                                rows={2}
                                className="rounded-lg"
                            />
                        </div>
                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setShowEventModal(false)}
                                className="rounded-full"
                            >
                                Annuler
                            </Button>
                            <Button type="submit" disabled={submitting} className="rounded-full">
                                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Créer'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default EditorialDetail;
